/**
 * Pure parser for the multi-tier TechDocs annotation contract (Story 6.2).
 *
 * Entity annotations in, canonical `ResolvedTierConfig` out, or a typed
 * validation failure — never an exception, never a silent fallback to
 * defaults (consistent with the ACL annotation pattern: malformed
 * configuration fails closed, because tier ids become blob paths and, in the
 * future, external-exposure boundaries).
 *
 * This module is kept dependency-free (plain objects, no Backstage/React
 * imports) and mirrors the inline Python resolver in
 * `.github/workflows/techdocs-publish.yml` rule for rule, in the same order,
 * so CI and Hub resolve identical tier lists. A unit test pins the shared
 * preset data in `presets.json` to the workflow's `TECHDOCS_TIER_PRESETS`
 * block, so the preset definitions cannot drift. The validation rules
 * themselves are mirrored by hand: when changing either side, port the change
 * to the other and spot-check it against the shared cases in
 * `__tests__/fixtures/` (see the README there).
 */
import presetsJson from './presets.json';
import {
  TierAudience,
  TierConfigMode,
  TierConfigResult,
  TierDefinition,
  TierValidationRule,
} from './types';

/** `univerus.com/techdocs-preset`: a named preset, e.g. `standard-3-tier`. */
export const TIER_PRESET_ANNOTATION = 'univerus.com/techdocs-preset';
/** `univerus.com/techdocs-tiers`: comma-separated tier ids, first is default. */
export const TIER_LIST_ANNOTATION = 'univerus.com/techdocs-tiers';
/** `univerus.com/techdocs-tiers-config`: full declarative JSON (as a string). */
export const TIER_CONFIG_ANNOTATION = 'univerus.com/techdocs-tiers-config';

const TIER_ID_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
/**
 * fnmatch equivalent of the reserved `v*.*.*` shape. Unreachable after the id
 * pattern (ids cannot contain dots) — kept as defence in depth, mirroring the
 * workflow resolver.
 */
const RESERVED_VERSION_SHAPE = /^v[^]*\.[^]*\.[^]*$/;
const PATH_SEGMENT_PATTERN = /^[A-Za-z0-9._-]+$/;
const AUDIENCES: readonly string[] = [
  'internal',
  'authenticated-external',
  'anonymous',
];
const TIER_KEYS = new Set([
  'id',
  'label',
  'description',
  'path',
  'icon',
  'default',
  'priority',
  'audience',
]);

/** The shared preset definitions — data, not code. */
const PRESETS: Record<string, Record<string, unknown>[]> = presetsJson;

export interface ParseTierConfigOptions {
  /**
   * Repo-relative docs root used to default tier paths (`<docsPath>/<id>`)
   * and as the implicit internal tier's path. Mirrors the workflow's
   * `docs-path` input. Defaults to `docs`.
   */
  docsPath?: string;
}

const failure = (
  rule: TierValidationRule,
  message: string,
): TierConfigResult => ({ ok: false, error: { rule, message } });

/** `posixpath.normpath` equivalent: collapse `.`, `..`, and empty segments. */
function normalizePosixPath(path: string): string {
  const isAbsolute = path.startsWith('/');
  const out: string[] = [];
  for (const segment of path.split('/')) {
    if (segment === '' || segment === '.') {
      continue;
    }
    if (segment === '..') {
      if (out.length > 0 && out[out.length - 1] !== '..') {
        out.pop();
      } else if (!isAbsolute) {
        out.push('..');
      }
    } else {
      out.push(segment);
    }
  }
  const joined = out.join('/');
  if (isAbsolute) {
    return `/${joined}`;
  }
  return joined || '.';
}

/** Python `str.capitalize()` equivalent for the label default. */
function capitalize(id: string): string {
  return id.charAt(0).toUpperCase() + id.slice(1);
}

type FieldTypes = 'string' | 'boolean' | 'number';

const FIELD_TYPES: Record<string, FieldTypes> = {
  id: 'string',
  label: 'string',
  description: 'string',
  path: 'string',
  icon: 'string',
  audience: 'string',
  default: 'boolean',
  priority: 'number',
};

/**
 * Parse an entity's tier annotations (`entity.metadata.annotations`) into the
 * canonical resolved tier configuration.
 *
 * Exactly one annotation mode may be present. No annotation resolves to the
 * single implicit `internal` tier (backward compatibility for untiered
 * repos). Any malformed configuration fails closed with a machine-readable
 * rule and a human-actionable message.
 */
export function parseTierConfig(
  annotations: Record<string, string | undefined> | undefined,
  options: ParseTierConfigOptions = {},
): TierConfigResult {
  const docsPath = normalizePosixPath(options.docsPath ?? 'docs');
  const ann = annotations ?? {};

  const modeAnnotations = [
    TIER_PRESET_ANNOTATION,
    TIER_LIST_ANNOTATION,
    TIER_CONFIG_ANNOTATION,
  ].filter(key => ann[key] !== undefined && ann[key] !== '');

  if (modeAnnotations.length > 1) {
    return failure(
      'multiple-modes',
      `exactly one tier annotation mode may be set, found: ${modeAnnotations.join(
        ', ',
      )}`,
    );
  }

  if (modeAnnotations.length === 0) {
    const implicit: TierDefinition = {
      id: 'internal',
      label: 'Internal',
      path: docsPath,
      default: true,
      audience: 'internal',
    };
    return {
      ok: true,
      config: {
        mode: 'implicit-internal',
        tiers: [implicit],
        defaultTier: 'internal',
      },
    };
  }

  let mode: TierConfigMode;
  let rawTiers: unknown[];
  if (modeAnnotations[0] === TIER_PRESET_ANNOTATION) {
    mode = 'preset';
    const name = ann[TIER_PRESET_ANNOTATION]!.trim();
    if (!(name in PRESETS)) {
      return failure(
        'unknown-preset',
        `unknown preset '${name}'. Known presets: ${Object.keys(PRESETS)
          .sort()
          .join(', ')}`,
      );
    }
    rawTiers = PRESETS[name].map(tier => ({ ...tier }));
  } else if (modeAnnotations[0] === TIER_LIST_ANNOTATION) {
    mode = 'simple-list';
    const ids = ann[TIER_LIST_ANNOTATION]!.split(',')
      .map(id => id.trim())
      .filter(id => id !== '');
    rawTiers = ids.map((id, index) => ({ id, default: index === 0 }));
  } else {
    mode = 'full-config';
    let parsed: unknown;
    try {
      parsed = JSON.parse(ann[TIER_CONFIG_ANNOTATION]!);
    } catch (error) {
      return failure(
        'invalid-json',
        `${TIER_CONFIG_ANNOTATION} is not valid JSON: ${error}`,
      );
    }
    if (!Array.isArray(parsed)) {
      return failure(
        'not-an-array',
        `${TIER_CONFIG_ANNOTATION} must be a JSON array of tier objects`,
      );
    }
    rawTiers = parsed;
  }

  const seen = new Set<string>();
  let defaults = 0;
  const resolved: TierDefinition[] = [];

  for (const raw of rawTiers) {
    if (
      raw === null ||
      typeof raw !== 'object' ||
      Array.isArray(raw) ||
      !(raw as { id?: unknown }).id
    ) {
      return failure(
        'invalid-tier-shape',
        'every tier must be an object with an id',
      );
    }
    const tier = raw as Record<string, unknown>;

    // Unknown keys and mistyped fields fail closed — a typo like "defualt"
    // must not silently change what gets published.
    const unknown = Object.keys(tier)
      .filter(key => !TIER_KEYS.has(key))
      .sort();
    if (unknown.length > 0) {
      return failure(
        'unknown-key',
        `tier '${tier.id}': unknown key(s) ${unknown.join(
          ', ',
        )} - allowed keys: ${[...TIER_KEYS].sort().join(', ')}`,
      );
    }
    for (const [key, expected] of Object.entries(FIELD_TYPES)) {
      const value = tier[key];
      if (value !== undefined && value !== null && typeof value !== expected) {
        return failure(
          'invalid-type',
          `tier '${tier.id}': '${key}' must be a ${expected}`,
        );
      }
    }

    const id = tier.id as string;
    if (!TIER_ID_PATTERN.test(id)) {
      return failure(
        'invalid-id',
        `tier id '${id}' must match ^[a-z0-9][a-z0-9-]*$`,
      );
    }
    if (id === 'latest' || RESERVED_VERSION_SHAPE.test(id)) {
      return failure(
        'reserved-id',
        `tier id '${id}' is reserved - 'latest' and v*.*.* shapes would shadow the version namespace in URLs and blob paths`,
      );
    }
    if (seen.has(id)) {
      return failure('duplicate-id', `duplicate tier id '${id}'`);
    }
    seen.add(id);

    const audience = (tier.audience ?? 'internal') as string;
    if (!AUDIENCES.includes(audience)) {
      return failure(
        'invalid-audience',
        `tier '${id}': audience must be one of ${[...AUDIENCES]
          .sort()
          .join(', ')}, got '${audience}'`,
      );
    }
    if (id === 'internal' && audience !== 'internal') {
      return failure(
        'reserved-internal-audience',
        "tier 'internal' is reserved as the implicit-default tier and must keep audience: internal",
      );
    }

    const rawPath = (tier.path as string | undefined) || `${docsPath}/${id}`;
    const path = normalizePosixPath(rawPath);
    // '.' (e.g. from 'docs/..') would root the tier at the repository itself,
    // containing every other tier's path without tripping the overlap check.
    if (
      path === '.' ||
      path.startsWith('..') ||
      path.startsWith('/') ||
      rawPath.includes('\\')
    ) {
      return failure(
        'invalid-path',
        `tier '${id}': path '${rawPath}' must be a relative path inside the repository (not the repository root)`,
      );
    }
    if (!path.split('/').every(segment => PATH_SEGMENT_PATTERN.test(segment))) {
      return failure(
        'unsafe-path',
        `tier '${id}': path '${rawPath}' contains segments that are not URL-safe - every segment must match [A-Za-z0-9._-]+`,
      );
    }

    const isDefault = (tier.default as boolean | undefined) ?? false;
    if (isDefault) {
      defaults += 1;
    }

    const label = (tier.label as string | undefined) || capitalize(id);
    const description = tier.description as string | undefined;
    const icon = tier.icon as string | undefined;
    const priority = tier.priority as number | undefined;

    resolved.push({
      id,
      label,
      ...(description !== undefined && description !== null
        ? { description }
        : {}),
      path,
      ...(icon !== undefined && icon !== null ? { icon } : {}),
      default: isDefault,
      ...(priority !== undefined && priority !== null ? { priority } : {}),
      audience: audience as TierAudience,
    });
  }

  if (resolved.length === 0) {
    return failure('zero-tiers', 'tier configuration resolved to zero tiers');
  }
  if (defaults === 0) {
    resolved[0].default = true;
  } else if (defaults > 1) {
    return failure(
      'multiple-defaults',
      'exactly one tier may set default: true',
    );
  }

  // Tier paths must be disjoint: each tier's subtree is built and published
  // as a self-contained site, so equal or nested paths would let one tier's
  // build destroy or absorb another tier's sources.
  for (let i = 0; i < resolved.length; i++) {
    for (let j = i + 1; j < resolved.length; j++) {
      const a = resolved[i];
      const b = resolved[j];
      if (
        a.path === b.path ||
        a.path.startsWith(`${b.path}/`) ||
        b.path.startsWith(`${a.path}/`)
      ) {
        return failure(
          'overlapping-paths',
          `tiers '${a.id}' and '${b.id}': paths '${a.path}' and '${b.path}' overlap - tier paths must be disjoint folders`,
        );
      }
    }
  }

  return {
    ok: true,
    config: {
      mode,
      tiers: resolved,
      defaultTier: resolved.find(tier => tier.default)!.id,
    },
  };
}
